"use client";

import { AspectRatio } from "@repo/ui/aspect-ratio";
import { Button } from "@repo/ui/button";
import { CloudinaryImage } from "@repo/ui/cloudinary-image";
import * as Grid from "@repo/ui/grid";
import * as List from "@repo/ui/list";
import { VideoPlayer } from "@repo/ui/video-player";
import { Zoomable } from "@repo/ui/zoomable";
import { ImageSizes } from "@repo/utils/image-presets";
import classes from "./gallery.module.css";

export type GalleryProps = {
  productImages: string[];
  productName: string;
  videoUrl?: string;
  autoPlayVideos?: boolean;
  sourceSetProfile: ImageSizes;
  aspectRatio?: number | "auto";
  itemAspectRatio: number;
  sourceSetSizes: string;
  scrollDirection?: "vertical" | "horizontal";
  onImageClick?: (index: number) => void;
  zoomImages?: boolean;
  columns?: number;
};

export const Gallery = ({
  productImages,
  productName,
  videoUrl,
  sourceSetProfile,
  sourceSetSizes,
  aspectRatio,
  itemAspectRatio,
  scrollDirection,
  onImageClick,
  zoomImages = true,
  autoPlayVideos = false,
  columns = 1,
}: GalleryProps) => {
  const productImagesWithVideo =
    videoUrl && videoUrl !== ""
      ? [...productImages.slice(0, 1), "video", ...productImages.slice(1)]
      : productImages;

  const galleryList = (
    <Grid.Container columns={columns} asChild>
      <List.Root
        snap
        {...(scrollDirection === "horizontal"
          ? { direction: "horizontal" }
          : { direction: "vertical" })}
        className={classes.galleryList}
      >
        {productImagesWithVideo.map((productImage, i) => {
          const imageTemplate = (
            <CloudinaryImage
              alt={`Model wearing ${productName}`}
              loading="eager"
              url={productImage}
              srcSize={240}
              sourceSetProfile={sourceSetProfile}
              sizes={sourceSetSizes}
            />
          );

          const image = (
            <AspectRatio ratio={itemAspectRatio}>
              {onImageClick ? (
                <Button
                  onClick={() => {
                    if (onImageClick) {
                      onImageClick(i);
                    }
                  }}
                  className={classes.imageButton}
                >
                  {imageTemplate}
                </Button>
              ) : (
                imageTemplate
              )}
            </AspectRatio>
          );

          const zoomableImage = <Zoomable>{image}</Zoomable>;

          return (
            <Grid.Item key={`productImage-${i}`} asChild>
              <List.Item>
                {productImage === "video" && videoUrl ? (
                  <AspectRatio ratio={itemAspectRatio}>
                    <VideoPlayer
                      src={videoUrl}
                      controls
                      autoPlay={autoPlayVideos}
                      loop={false}
                      muted
                      description={`Video showing model wearing ${productName}`}
                      className={classes.videoPlayer}
                    />
                  </AspectRatio>
                ) : zoomImages ? (
                  zoomableImage
                ) : (
                  image
                )}
              </List.Item>
            </Grid.Item>
          );
        })}
      </List.Root>
    </Grid.Container>
  );

  return aspectRatio ? (
    <AspectRatio ratio={aspectRatio}>{galleryList}</AspectRatio>
  ) : (
    galleryList
  );
};
